import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SERVICE_INTERESTS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-slate-900 border border-white/10 text-slate-100 placeholder:text-slate-600 rounded-none h-11 focus-visible:ring-1 focus-visible:ring-cyan-400 focus-visible:border-cyan-400 focus-visible:ring-offset-0";

export default function DemoDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      toast.success("Demo requested! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", service_interest: "" });
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? detail[0]?.msg || "Submission failed."
          : detail || "Submission failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="demo-dialog"
        className="bg-slate-950 border border-cyan-400/30 text-slate-100 rounded-none max-w-lg p-0"
      >
        <div className="p-7 md:p-8">
          <DialogHeader className="text-left mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 border border-cyan-400/40 bg-cyan-400/10 flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-cyan-300" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-mono">
                Schedule a Demo
              </span>
            </div>
            <DialogTitle className="text-2xl md:text-3xl tracking-tight font-medium text-slate-50">
              See GenX systems in action.
            </DialogTitle>
            <DialogDescription className="text-slate-400 mt-2">
              Book a 30-minute walkthrough with a solutions consultant.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-5" data-testid="demo-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                  Name *
                </Label>
                <Input
                  data-testid="demo-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Full name"
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                  Email *
                </Label>
                <Input
                  data-testid="demo-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@company.com"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                  Phone
                </Label>
                <Input
                  data-testid="demo-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+91 ..."
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                  Company
                </Label>
                <Input
                  data-testid="demo-company"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Organization"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                Service of Interest
              </Label>
              <Select
                value={form.service_interest}
                onValueChange={(v) => update("service_interest", v)}
              >
                <SelectTrigger
                  data-testid="demo-service"
                  className={`${inputCls} text-left`}
                >
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 text-slate-100 rounded-none">
                  {SERVICE_INTERESTS.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      className="focus:bg-cyan-400/10 focus:text-cyan-200 rounded-none"
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              data-testid="demo-submit"
              disabled={submitting}
              className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-none font-semibold h-12 btn-glow disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                </>
              ) : (
                <>
                  Request Demo <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
